import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import {
  getPurchaseSiteMigrationSummary,
  getPurchaseSiteMigrationPending,
  getPurchaseSiteMigrationFilterOptions,
  getPurchaseSiteMigrationPendingIds,
  bulkAssignPurchaseSite,
} from '../../../services/purchaseSiteMigrationService';
import { getPurchaseSites } from '../../../services/purchaseSitesService';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';

const TYPE_LABELS = {
  purchase_order: 'Orden de compra',
  service_order: 'Orden de servicio',
  requirement: 'Requisición',
  document: 'Documento',
  continuous_service: 'Servicio continuo',
};

const PER_PAGE = 50;

const rowKey = (row) => `${row.type}:${row.id}`;

const buildListParams = ({
  typeFilter,
  page,
  searchApplied,
  supplierFilter,
  userFilter,
}) => ({
  type: typeFilter,
  page,
  per_page: PER_PAGE,
  search: searchApplied.trim() || undefined,
  supplier_id: supplierFilter || undefined,
  user_id: userFilter || undefined,
});

const PurchaseSiteAssignment = () => {
  const { comprasPurchaseSitesEnabled } = useModuleSettings();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});
  const [targetSiteId, setTargetSiteId] = useState('');
  const [propagate, setPropagate] = useState(true);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ suppliers: [], users: [] });
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });
  const [selectingAll, setSelectingAll] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadFilterOptions = useCallback(async () => {
    try {
      const res = await getPurchaseSiteMigrationFilterOptions({ type: typeFilter });
      if (res.success) {
        setFilterOptions({
          suppliers: res.data?.suppliers || [],
          users: res.data?.users || [],
        });
      }
    } catch {
      /* opciones auxiliares; no bloquear la pantalla */
    }
  }, [typeFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, pendingRes, sitesRes] = await Promise.all([
        getPurchaseSiteMigrationSummary(),
        getPurchaseSiteMigrationPending(buildListParams({
          typeFilter,
          page,
          searchApplied,
          supplierFilter,
          userFilter,
        })),
        getPurchaseSites({ active_only: true }),
      ]);
      if (sumRes.success) setSummary(sumRes.data);
      if (pendingRes.success) {
        setRows(pendingRes.data || []);
        setMeta(pendingRes.meta || { current_page: page, last_page: 1, per_page: PER_PAGE, total: 0 });
      }
      if (sitesRes.success) setSites(sitesRes.data || []);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar datos', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [typeFilter, page, searchApplied, supplierFilter, userFilter]);

  const applySearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
    setSupplierFilter('');
    setUserFilter('');
    setPage(1);
  };

  const handleSupplierFilterChange = (value) => {
    setSupplierFilter(value);
    setPage(1);
  };

  const handleUserFilterChange = (value) => {
    setUserFilter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchApplied('');
    setSupplierFilter('');
    setUserFilter('');
    setTypeFilter('all');
    setPage(1);
  };

  useEffect(() => {
    if (comprasPurchaseSitesEnabled) loadFilterOptions();
  }, [comprasPurchaseSitesEnabled, loadFilterOptions]);

  useEffect(() => {
    if (comprasPurchaseSitesEnabled) loadData();
  }, [comprasPurchaseSitesEnabled, loadData]);

  const toggleRow = (row) => {
    const key = rowKey(row);
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = row;
      return next;
    });
  };

  const mergeRowsIntoSelection = (items) => {
    if (!items.length) return;
    setSelectedIds((prev) => {
      const next = { ...prev };
      items.forEach((row) => {
        next[rowKey(row)] = row;
      });
      return next;
    });
  };

  const selectCurrentPage = () => {
    mergeRowsIntoSelection(rows);
  };

  const deselectCurrentPage = () => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        delete next[rowKey(row)];
      });
      return next;
    });
  };

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const res = await getPurchaseSiteMigrationPendingIds({
        type: typeFilter,
        search: searchApplied.trim() || undefined,
        supplier_id: supplierFilter || undefined,
        user_id: userFilter || undefined,
      });
      if (!res.success) throw new Error('No se pudo cargar la selección');
      const items = res.data || [];
      mergeRowsIntoSelection(items.map((item) => ({
        type: item.type,
        id: item.id,
        code: item.code || `${item.type}-${item.id}`,
        supplier_name: item.supplier_name,
        user_name: item.user_name,
      })));
      const truncated = res.meta?.truncated;
      setToast({
        isOpen: true,
        message: truncated
          ? `Se seleccionaron ${items.length} documento(s) (límite alcanzado).`
          : `Se seleccionaron ${items.length} documento(s) con los filtros actuales.`,
        type: truncated ? 'warning' : 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'No se pudo seleccionar todos los documentos',
        type: 'error',
      });
    } finally {
      setSelectingAll(false);
    }
  };

  const selectBySupplier = async () => {
    if (!supplierFilter) {
      setToast({ isOpen: true, message: 'Elija una entidad en el filtro', type: 'warning' });
      return;
    }
    await selectAllFiltered();
  };

  const selectByUser = async () => {
    if (!userFilter) {
      setToast({ isOpen: true, message: 'Elija un usuario en el filtro', type: 'warning' });
      return;
    }
    await selectAllFiltered();
  };

  const clearSelection = () => {
    setSelectedIds({});
  };

  const handleAssign = async () => {
    const items = Object.values(selectedIds);
    if (!items.length || !targetSiteId) {
      setToast({ isOpen: true, message: 'Seleccione documentos y una sede destino', type: 'warning' });
      return;
    }
    setAssigning(true);
    try {
      const res = await bulkAssignPurchaseSite({
        purchase_site_id: Number(targetSiteId),
        items: items.map((r) => ({ type: r.type, id: r.id })),
        propagate,
      });
      if (res.success) {
        const warnings = res.data?.warnings || [];
        setToast({
          isOpen: true,
          message: warnings.length
            ? `${res.message} (${warnings.length} advertencia(s))`
            : res.message,
          type: warnings.length ? 'warning' : 'success',
        });
        setSelectedIds({});
        await Promise.all([loadData(), loadFilterOptions()]);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al asignar',
        type: 'error',
      });
    } finally {
      setAssigning(false);
    }
  };

  if (!comprasPurchaseSitesEnabled) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Active las sedes de compra en Configuraciones generales.</p>
      </div>
    );
  }

  const withoutTotal = summary?.without_total ?? 0;
  const guidesExcluded = summary?.guides_without_site_excluded ?? 0;
  const selectedCount = Object.keys(selectedIds).length;
  const rangeFrom = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
  const rangeTo = Math.min(meta.current_page * meta.per_page, meta.total);
  const allPageSelected = rows.length > 0 && rows.every((row) => selectedIds[rowKey(row)]);
  const somePageSelected = rows.some((row) => selectedIds[rowKey(row)]);
  const hasActiveFilters = Boolean(
    searchApplied || supplierFilter || userFilter || typeFilter !== 'all'
  );

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Link to="/purchase-orders" className="text-teal-600 text-sm hover:underline">
            ← Volver a compras
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 mt-2">Asignar sede a documentos históricos</h1>
          <p className="text-gray-600 mt-1">
            Documentos de compra sin sede: <strong>{withoutTotal}</strong>. Seleccione y asigne a una sede.
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Las guías de ingreso no se listan aquí: ya pertenecen a su almacén y se filtran por sede según ese almacén.
            {guidesExcluded > 0 ? ` (${guidesExcluded} guía(s) fuera de este listado.)` : ''}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Buscar</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                placeholder="Código, proveedor, serie, usuario…"
                className="border border-gray-300 rounded-lg px-3 py-2 min-w-[220px]"
              />
              <button
                type="button"
                onClick={applySearch}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Buscar
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Tipo</label>
            <select
              value={typeFilter}
              onChange={(e) => handleTypeFilterChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              {Object.entries(TYPE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Entidad</label>
            <select
              value={supplierFilter}
              onChange={(e) => handleSupplierFilterChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-w-[220px]"
            >
              <option value="">Todas las entidades</option>
              {filterOptions.suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Usuario</label>
            <select
              value={userFilter}
              onChange={(e) => handleUserFilterChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-w-[200px]"
            >
              <option value="">Todos los usuarios</option>
              {filterOptions.users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Sede destino</label>
            <select
              value={targetSiteId}
              onChange={(e) => setTargetSiteId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-w-[200px]"
            >
              <option value="">Seleccionar…</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={propagate}
              onChange={(e) => setPropagate(e.target.checked)}
            />
            Propagar a documentos vinculados (no guías)
          </label>
          <button
            type="button"
            onClick={handleAssign}
            disabled={assigning}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {assigning ? 'Asignando…' : `Asignar seleccionados${selectedCount ? ` (${selectedCount})` : ''}`}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium text-amber-900 mr-1">Selección:</span>
          <button
            type="button"
            onClick={allPageSelected ? deselectCurrentPage : selectCurrentPage}
            disabled={loading || rows.length === 0}
            className="px-3 py-1.5 text-sm border border-amber-300 rounded-lg bg-white hover:bg-amber-100 disabled:opacity-50"
          >
            {allPageSelected ? 'Quitar página' : 'Página actual'}
          </button>
          <button
            type="button"
            onClick={selectAllFiltered}
            disabled={loading || selectingAll || meta.total === 0}
            className="px-3 py-1.5 text-sm border border-amber-300 rounded-lg bg-white hover:bg-amber-100 disabled:opacity-50"
          >
            {selectingAll ? 'Seleccionando…' : `Todos (filtros)${meta.total ? ` · ${meta.total}` : ''}`}
          </button>
          <button
            type="button"
            onClick={selectBySupplier}
            disabled={loading || selectingAll || !supplierFilter}
            className="px-3 py-1.5 text-sm border border-amber-300 rounded-lg bg-white hover:bg-amber-100 disabled:opacity-50"
          >
            Por entidad
          </button>
          <button
            type="button"
            onClick={selectByUser}
            disabled={loading || selectingAll || !userFilter}
            className="px-3 py-1.5 text-sm border border-amber-300 rounded-lg bg-white hover:bg-amber-100 disabled:opacity-50"
          >
            Por usuario
          </button>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              Limpiar ({selectedCount})
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allPageSelected && somePageSelected;
                    }}
                    onChange={() => (allPageSelected ? deselectCurrentPage() : selectCurrentPage())}
                    disabled={loading || rows.length === 0}
                    title="Seleccionar página actual"
                  />
                </th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Código</th>
                <th className="px-3 py-2 text-left">Entidad</th>
                <th className="px-3 py-2 text-left">Serie y número</th>
                <th className="px-3 py-2 text-left">Usuario</th>
                <th className="px-3 py-2 text-left">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No hay documentos sin sede</td></tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  return (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedIds[key])}
                          onChange={() => toggleRow(row)}
                        />
                      </td>
                      <td className="px-3 py-2">{TYPE_LABELS[row.type] || row.type}</td>
                      <td className="px-3 py-2 font-medium">{row.code}</td>
                      <td className="px-3 py-2">{row.supplier_name || '—'}</td>
                      <td className="px-3 py-2">{row.series_number || '—'}</td>
                      <td className="px-3 py-2">{row.user_name || '—'}</td>
                      <td className="px-3 py-2">{row.date || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {!loading && meta.total > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Mostrando {rangeFrom}–{rangeTo} de {meta.total} documento(s)
                {selectedCount > 0 ? ` · ${selectedCount} seleccionado(s)` : ''}
              </p>
              {meta.last_page > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={meta.current_page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {meta.current_page} de {meta.last_page}
                  </span>
                  <button
                    type="button"
                    disabled={meta.current_page >= meta.last_page}
                    onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default PurchaseSiteAssignment;
